import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/feature/PageHero";
import CtaBand from "@/components/feature/CtaBand";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageMeta from "@/hooks/usePageMeta";
import { channels, contactEmail, seoKeywords } from "@/mocks/siteData";
import ContactForm from "@/pages/contact/components/ContactForm";

const pageTitle = "联系我们 - 匿名私信投稿平台咨询与反馈 | 潜声投稿";
const pageDesc =
  "联系潜声投稿：通过匿名私信投稿通道或咨询表单与我们取得联系。文中说明投稿咨询、隐私与安全、素材格式、合作建议等问题的处理方式与响应时间。";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: pageTitle,
    description: pageDesc,
    inLanguage: "zh-CN",
    url: "https://qiansheng.online/contact",
    mainEntity: {
      "@type": "Organization",
      name: "潜声投稿",
      email: contactEmail,
      description: "面向公众的匿名爆料投稿平台，提供四类安全投稿通道与投稿咨询。",
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "投稿咨询",
          email: contactEmail,
          availableLanguage: ["zh-CN"],
        },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://qiansheng.online/" },
      { "@type": "ListItem", position: 2, name: "联系我们", item: "https://qiansheng.online/contact" },
    ],
  },
];

const contactCards = [
  {
    id: "cc1",
    icon: "ri-mail-line",
    title: "投稿与咨询邮箱",
    value: contactEmail,
    desc: "适合非敏感的投稿咨询与内容更正请求，回复周期为工作日 1 至 2 天。",
  },
  {
    id: "cc2",
    icon: "ri-chat-private-line",
    title: "匿名私信通道",
    value: "无需注册 · 一对一沟通",
    desc: "涉及具体线索或希望匿名提问时，优先使用私信通道，不要通过邮箱传递敏感材料。",
  },
  {
    id: "cc3",
    icon: "ri-time-line",
    title: "响应时间说明",
    value: "工作日内平均 6 小时",
    desc: "深夜与节假日提交的线索会在下一个工作日统一处理，紧急人身安全事项请在描述中标注。",
  },
  {
    id: "cc4",
    icon: "ri-refund-2-line",
    title: "内容更正与撤回",
    value: "凭线索编号即可申请",
    desc: "若刊发内容与事实有出入，或你希望撤回素材，请附上线索编号，我们会尽快核实处理。",
  },
];

export default function Contact() {
  usePageMeta({
    title: pageTitle,
    description: pageDesc,
    keywords: seoKeywords,
    path: "/contact",
    jsonLd,
  });

  return (
    <Layout>
      <PageHero
        eyebrow="联系我们"
        title="有疑问，先说清楚再决定"
        subtitle="不确定自己的线索能不能用、不知道素材该怎么交、担心隐私问题——这些都可以先问。提问本身不需要暴露任何身份。"
        image="https://readdy.ai/api/search-image?query=Abstract%20dark%20background%20with%20a%20single%20soft%20glowing%20envelope%20like%20geometric%20shape%20made%20of%20light%20lines%2C%20deep%20teal%20base%2C%20warm%20amber%20accents%2C%20minimal%20editorial%20composition%2C%20generous%20negative%20space%2C%20high%20contrast%2C%20no%20text%2C%20no%20people&width=1600&height=700&seq=qs-contact-a1&orientation=landscape"
        imageAlt="由光线构成的抽象信封图形，象征与潜声投稿编辑的匿名沟通渠道"
        crumbs={[{ label: "首页", to: "/" }, { label: "联系我们" }]}
      />

      <section className="w-full px-4 md:px-8 pt-14 md:pt-20">
        <div className="max-w-content mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {contactCards.map((card, index) => (
            <Reveal key={card.id} delay={index * 70}>
              <div className="h-full p-6 rounded-lg bg-background-100 border border-background-200">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 text-xl">
                  <i className={card.icon}></i>
                </span>
                <h2 className="mt-5 font-heading text-base font-bold text-foreground-950">
                  {card.title}
                </h2>
                <p className="mt-2 text-sm font-medium text-primary-600 break-all">{card.value}</p>
                <p className="mt-3 text-xs leading-relaxed text-foreground-500">{card.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionHeading
                eyebrow="咨询表单"
                title="写下你的问题"
                desc="这个表单适合投稿前的疑问、隐私安全咨询与内容更正请求。如果你要提交的是具体线索，请直接使用投稿通道，避免线索信息散落在咨询里。"
              />
            </Reveal>

            <Reveal delay={80}>
              <div className="mt-8 p-5 rounded-lg bg-accent-50 border border-accent-200">
                <p className="flex items-start gap-3 text-sm leading-relaxed text-accent-900">
                  <i className="ri-alert-line text-lg mt-0.5"></i>
                  请勿在咨询表单中直接粘贴敏感线索细节。涉及具体人物与事件的描述，请通过投稿通道提交。
                </p>
              </div>
            </Reveal>

            <Reveal delay={140}>
              <div className="mt-8">
                <h3 className="m-0 font-heading text-base font-semibold text-foreground-950">
                  想直接投稿？
                </h3>
                <ul className="mt-4 space-y-2.5">
                  {channels.map((channel) => (
                    <li key={channel.id}>
                      <Link
                        to={`/channels#${channel.slug}`}
                        className="group flex items-center gap-3 text-sm text-foreground-700 hover:text-primary-700 transition-colors cursor-pointer"
                      >
                        <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 text-primary-600 border border-background-200">
                          <i className={`${channel.icon} text-sm`}></i>
                        </span>
                        {channel.name}
                        <i className="ri-arrow-right-line opacity-0 group-hover:opacity-100 transition-opacity"></i>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={80}>
              <ContactForm />
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand
        title="问题已经问完了，就行动吧"
        desc="四类投稿通道全部支持匿名，不需要注册、不需要实名。挑一条最适合你手上素材的通道开始。"
        primaryLabel="前往投稿通道"
        primaryTo="/channels"
        secondaryLabel="阅读安全指南"
        secondaryTo="/guide"
      />
    </Layout>
  );
}